import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Checkbox, 
    FormControlLabel,
    IconButton,
    Alert,
    Grid,
    Divider,
    CircularProgress,
    Tabs,
    Tab,
    Chip,
    Paper,
    Autocomplete
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { updateStrategy } from '../../api/strategyApi';
import { getActiveSymbols } from '../../api/symbolApi';

// Validation schema
const validationSchema = yup.object({
    name: yup
        .string()
        .required('Strategy name is required')
        .min(1, 'Name must be at least 1 character')
        .max(100, 'Name cannot exceed 100 characters')
        .matches(/^[a-zA-Z0-9\s\-_]+$/, 'Name can only contain letters, numbers, spaces, hyphens, and underscores'),
    description: yup
        .string()
        .max(500, 'Description cannot exceed 500 characters')
        .nullable(),
    strategyType: yup
        .string()
        .required('Strategy type is required')
        .max(50, 'Strategy type cannot exceed 50 characters'),
    isMultiSymbol: yup.boolean(),
    isEnabled: yup.boolean(),
    openNow: yup.string().oneOf(['buy', 'sell', 'none'], 'Invalid Open Now option')
});

const EditStrategyModal = ({ open, onClose, onSuccess, strategy, formVersion = 'v1', selectedAccount }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [symbols, setSymbols] = useState([]);
    const [originalSymbols, setOriginalSymbols] = useState([]); // Track original symbols
    const [newSymbol, setNewSymbol] = useState('');
    const [activeSymbols, setActiveSymbols] = useState([]);
    const [symbolsLoading, setSymbolsLoading] = useState(false);

    // Strategy types for dropdown
    const strategyTypes = [
        'goldarb'
    ];

    // Initialize form with strategy data
    const formik = useFormik({
        initialValues: {
            name: strategy?.name || '',
            description: strategy?.description || '',
            strategyType: strategy?.strategyType || 'goldarb',
            mode: strategy?.parameters?.Mode || 'Demo',
            isMultiSymbol: strategy?.isMultiSymbol !== undefined ? strategy.isMultiSymbol :
                           strategy?.parameters?.isMultiSymbol !== undefined ? strategy.parameters.isMultiSymbol : false,
            isEnabled: strategy?.isEnabled !== undefined ? strategy.isEnabled : 
                       strategy?.parameters?.Enabled !== undefined ? strategy.parameters.Enabled :
                       strategy?.parameters?.isEnabled !== undefined ? strategy.parameters.isEnabled :
                       strategy?.parameters?.enabled !== undefined ? strategy.parameters.enabled :
                       strategy?.enabled !== undefined ? strategy.enabled : true,
            // Open Now settings
            openNow: strategy?.parameters?.OpenNow_BuySpread ? 'buy' : 
                     strategy?.parameters?.OpenNow_SellSpread ? 'sell' : 'none',
            // Grid Management parameters - using flat structure from database
            spreadDirection: strategy?.parameters?.spreadDirection || strategy?.parameters?.GridManagement?.SpreadDirection || 'Buy Spread',
            baseLotSize: strategy?.parameters?.baseLotSize?.toString() || strategy?.parameters?.GridManagement?.BaseLotSize?.toString() || '0.01',
            minLotSize: strategy?.parameters?.minLotSize?.toString() || strategy?.parameters?.GridManagement?.MinLotSize?.toString() || '0.01',
            maxLotOpenedAllowed: strategy?.parameters?.maxLotOpenedAllowed?.toString() || strategy?.parameters?.GridManagement?.MaxLotOpenedAllowed?.toString() || '0.05',
            gridThreshold: strategy?.parameters?.gridThreshold?.toString() || strategy?.parameters?.GridManagement?.GridThreshold?.toString() || '2',
            maxGridLevels: strategy?.parameters?.maxGridLevels?.toString() || strategy?.parameters?.GridManagement?.MaxGridLevels?.toString() || '3',
            tpGridPips: strategy?.parameters?.tpGridPips?.toString() || strategy?.parameters?.GridManagement?.TPGridPips?.toString() || '10',
            stopLossPips: strategy?.parameters?.stopLossPips?.toString() || strategy?.parameters?.GridManagement?.StopLossPips?.toString() || '20',
            tpGridEnabled: strategy?.parameters?.tpGridEnabled || strategy?.parameters?.GridManagement?.TPGridEnabled || false,
            stopLossEnabled: strategy?.parameters?.stopLossEnabled || strategy?.parameters?.GridManagement?.StopLossEnabled || false,
            allowGridReopening: strategy?.parameters?.allowGridReopening || strategy?.parameters?.GridManagement?.AllowGridReopening || false,
            resetGridOnFullClose: strategy?.parameters?.resetGridOnFullClose || strategy?.parameters?.GridManagement?.ResetGridOnFullClose || false,
            // Risk Management parameters - using flat structure from database
            riskManagementEnabled: strategy?.parameters?.['RiskManagement.Enabled'] || strategy?.parameters?.riskManagementEnabled || strategy?.parameters?.RiskManagement?.Enabled || false,
            warmupMinutes: strategy?.parameters?.warmupMinutes?.toString() || strategy?.parameters?.RiskManagement?.WarmupMinutes?.toString() || '0',
            maxOrdersPerDay: strategy?.parameters?.maxOrdersPerDay?.toString() || strategy?.parameters?.RiskManagement?.MaxOrdersPerDay?.toString() || '100',
            orderCooldownMs: strategy?.parameters?.orderCooldownMs?.toString() || strategy?.parameters?.RiskManagement?.OrderCooldownMs?.toString() || '220',
            orderCooldownSeconds: strategy?.parameters?.orderCooldownSeconds?.toString() || strategy?.parameters?.RiskManagement?.OrderCooldownSeconds?.toString() || '0',
            checkExistingPosition: strategy?.parameters?.checkExistingPosition || strategy?.parameters?.RiskManagement?.CheckExistingPosition || true,
            maxOpenOrdersPerSide: strategy?.parameters?.maxOpenOrdersPerSide?.toString() || strategy?.parameters?.RiskManagement?.MaxOpenOrdersPerSide?.toString() || '1',
            maxPositionDurationMinutes: strategy?.parameters?.maxPositionDurationMinutes?.toString() || strategy?.parameters?.RiskManagement?.MaxPositionDurationMinutes?.toString() || '1440',
            requireMinimumBars: strategy?.parameters?.requireMinimumBars || strategy?.parameters?.RiskManagement?.RequireMinimumBars || false,
            logRejectedOrders: strategy?.parameters?.logRejectedOrders || strategy?.parameters?.RiskManagement?.LogRejectedOrders || true,
            logMetricsEveryNthTick: strategy?.parameters?.logMetricsEveryNthTick?.toString() || strategy?.parameters?.RiskManagement?.LogMetricsEveryNthTick?.toString() || '100',
            // Spread Validation parameters - using flat structure from database
            minAcceptableSpread: strategy?.parameters?.['SpreadValidation.MinAcceptableSpread']?.toString() || strategy?.parameters?.minAcceptableSpread?.toString() || strategy?.parameters?.SpreadValidation?.MinAcceptableSpread?.toString() || '-1000',
            maxAcceptableSpread: strategy?.parameters?.['SpreadValidation.MaxAcceptableSpread']?.toString() || strategy?.parameters?.maxAcceptableSpread?.toString() || strategy?.parameters?.SpreadValidation?.MaxAcceptableSpread?.toString() || '1000',
            // EMA parameters - using flat structure from database
            emaEnabled: strategy?.parameters?.emaEnabled || strategy?.parameters?.EMA?.Enabled || false,
            emaPeriod: strategy?.parameters?.emaPeriod?.toString() || strategy?.parameters?.EMA?.Period?.toString() || '3',
            requireBarsForInit: strategy?.parameters?.requireBarsForInit?.toString() || strategy?.parameters?.RequireBarsForInit?.toString() || '1',
            medianFilterSize: strategy?.parameters?.medianFilterSize?.toString() || strategy?.parameters?.MedianFilterSize?.toString() || '5',
            // History Management parameters - using flat structure from database
            barPeriod: strategy?.parameters?.barPeriod?.toString() || strategy?.parameters?.History?.BarPeriod?.toString() || '60',
            barsToKeep: strategy?.parameters?.barsToKeep?.toString() || strategy?.parameters?.History?.BarsToKeep?.toString() || '100',
            enableDataCollection: strategy?.parameters?.enableDataCollection || strategy?.parameters?.History?.EnableDataCollection || false,
            saveHistoricalBarsToDatabase: strategy?.parameters?.saveHistoricalBarsToDatabase || strategy?.parameters?.History?.SaveHistoricalBarsToDatabase || false,
            // Logging Configuration parameters - using flat structure from database
            loggingEnabled: strategy?.parameters?.loggingEnabled || strategy?.parameters?.Logging?.Enabled || false,
            logLevel: strategy?.parameters?.logLevel || strategy?.parameters?.Logging?.LogLevel || 'Debug',
            logEveryNthTick: strategy?.parameters?.logEveryNthTick?.toString() || strategy?.parameters?.Logging?.LogEveryNthTick?.toString() || '100',
            orderTimeInForce: strategy?.parameters?.orderTimeInForce || strategy?.parameters?.OrderTimeInForce || 'GTC',
            // Additional fields from database
            useEma: strategy?.parameters?.UseEMA || false,
            lotSizeMultiplier: strategy?.parameters?.GridManagement?.LotSizeMultiplier?.toString() || '1.5',
            requireMinimumBars: strategy?.parameters?.RiskManagement?.RequireMinimumBars || false,
            openNowBuySpread: strategy?.parameters?.OpenNow_BuySpread || false,
            openNowSellSpread: strategy?.parameters?.OpenNow_SellSpread || false,
            // EMA additional fields
            emaMinBarsRequired: strategy?.parameters?.EMA?.MinBarsRequired?.toString() || '1',
            emaBarTimeframeMinutes: strategy?.parameters?.EMA?.BarTimeframeMinutes?.toString() || '1',
            // Symbols parameters
            spotSymbol: strategy?.parameters?.SpotSymbol || '',
            futureSymbol: strategy?.parameters?.FuturesSymbol || '',
            // V2-specific fields (flat structure)
            bidirectionalMode: strategy?.parameters?.['GridManagement.BidirectionalMode'] || false,
            maxTotalLotBothDirections: strategy?.parameters?.['GridManagement.MaxTotalLotBothDirections']?.toString() || '0.30',
            useBenchmarkMode: strategy?.parameters?.['GridManagement.UseBenchmarkMode'] || false,
            benchmarkThresholdBuy: strategy?.parameters?.['GridManagement.BenchmarkThresholdBuy']?.toString() || '4.0',
            benchmarkThresholdSell: strategy?.parameters?.['GridManagement.BenchmarkThresholdSell']?.toString() || '4.0',
            v2GridThreshold: strategy?.parameters?.['GridManagement.GridThreshold']?.toString() || '2.0',
            v2MaxGridLevels: strategy?.parameters?.['GridManagement.MaxGridLevels']?.toString() || '5',
            buyBaseLotSize: strategy?.parameters?.['GridManagement.BuyBaseLotSize']?.toString() || '0.01',
            sellBaseLotSize: strategy?.parameters?.['GridManagement.SellBaseLotSize']?.toString() || '0.01',
            buyLotSizeMultiplier: strategy?.parameters?.['GridManagement.BuyLotSizeMultiplier']?.toString() || '1.2',
            sellLotSizeMultiplier: strategy?.parameters?.['GridManagement.SellLotSizeMultiplier']?.toString() || '1.2',
            buyMaxLotOpenedAllowed: strategy?.parameters?.['GridManagement.BuyMaxLotOpenedAllowed']?.toString() || '0.15',
            sellMaxLotOpenedAllowed: strategy?.parameters?.['GridManagement.SellMaxLotOpenedAllowed']?.toString() || '0.15',
            v2MaxLotOpenedAllowed: strategy?.parameters?.['GridManagement.MaxLotOpenedAllowed']?.toString() || '0.15',
            v2TpGridEnabled: strategy?.parameters?.['GridManagement.TPGridEnabled'] || false,
            v2TpGridPips: strategy?.parameters?.['GridManagement.TPGridPips']?.toString() || '10.0',
            tpMode: strategy?.parameters?.['GridManagement.TPMode'] || 'AverageSpread',
            v2StopLossEnabled: strategy?.parameters?.['GridManagement.StopLossEnabled'] || false,
            v2StopLossPips: strategy?.parameters?.['GridManagement.StopLossPips']?.toString() || '18.0',
            spreadValidationEnabled: strategy?.parameters?.['SpreadValidation.Enabled'] || strategy?.parameters?.spreadValidationEnabled || false,
            // New strategy input fields
            buyGrid: strategy?.parameters?.['GridManagement.BuyGrid']?.toString() || '1.500',
            sellGrid: strategy?.parameters?.['GridManagement.SellGrid']?.toString() || '1.500',
            buyLegTP: strategy?.parameters?.['GridManagement.BuyLegTP']?.toString() || '0.300',
            sellLegTP: strategy?.parameters?.['GridManagement.SellLegTP']?.toString() || '0.300',
            buyLegSL: strategy?.parameters?.['GridManagement.BuyLegSL']?.toString() || '0.500',
            sellLegSL: strategy?.parameters?.['GridManagement.SellLegSL']?.toString() || '0.500',
            accumulativeTP: strategy?.parameters?.['GridManagement.AccumulativeTP']?.toString() || '2.5',
            accumulativeSL: strategy?.parameters?.['GridManagement.AccumulativeSL']?.toString() || '-10.0'
        },
        validationSchema: validationSchema,
        enableReinitialize: true, // Allow form to update when strategy data changes
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);

            try {
                // Get token from localStorage
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                // Validate that we have a strategy ID
                if (!strategy.id) {
                    throw new Error('Strategy ID is missing. Cannot update strategy without ID.');
                }

                // Get current user info for lastModifiedBy
                const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
                const lastModifiedBy = userInfo.email || userInfo.firstName || 'Unknown User';

                // Use the dynamic symbols array (no default - user must select symbols)
                const symbolsArray = symbols; // Empty array if no symbols selected

                // Validate that at least one symbol is selected
                if (!symbolsArray || symbolsArray.length === 0) {
                    toast.error('Please select at least one symbol before updating the strategy', {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setLoading(false);
                    return; // Stop form submission
                }

                // Prepare parameters object based on form version
                let parameters;

                if (formVersion === 'v2') {
                    // V2 PARAMETERS - FLAT STRUCTURE
                    parameters = {
                        version: "v2",
                        Enabled: Boolean(values.isEnabled),
                        Mode: values.mode === 'Demo' ? 1 : 0,
                        FuturesSymbol: values.futureSymbol || symbolsArray[1] || '',
                        SpotSymbol: values.spotSymbol || symbolsArray[0] || '',
                        "GridManagement.BidirectionalMode": Boolean(values.bidirectionalMode),
                        "GridManagement.MaxTotalLotBothDirections": parseFloat(values.maxTotalLotBothDirections) || 0.30,
                        "GridManagement.UseBenchmarkMode": Boolean(values.useBenchmarkMode),
                        "GridManagement.BenchmarkThresholdBuy": parseFloat(values.benchmarkThresholdBuy) || 4.0,
                        "GridManagement.BenchmarkThresholdSell": parseFloat(values.benchmarkThresholdSell) || 4.0,
                        "GridManagement.GridThreshold": parseFloat(values.v2GridThreshold) || 2.0,
                        "GridManagement.MaxGridLevels": parseInt(values.v2MaxGridLevels) || 5,
                        "GridManagement.BuyBaseLotSize": parseFloat(values.buyBaseLotSize) || 0.01,
                        "GridManagement.SellBaseLotSize": parseFloat(values.sellBaseLotSize) || 0.01,
                        "GridManagement.BuyLotSizeMultiplier": parseFloat(values.buyLotSizeMultiplier) || 1.2,
                        "GridManagement.SellLotSizeMultiplier": parseFloat(values.sellLotSizeMultiplier) || 1.2,
                        "GridManagement.BuyMaxLotOpenedAllowed": parseFloat(values.buyMaxLotOpenedAllowed) || 0.15,
                        "GridManagement.SellMaxLotOpenedAllowed": parseFloat(values.sellMaxLotOpenedAllowed) || 0.15,
                        "GridManagement.MaxLotOpenedAllowed": parseFloat(values.v2MaxLotOpenedAllowed) || 0.15,
                        "GridManagement.TPGridEnabled": Boolean(values.v2TpGridEnabled),
                        "GridManagement.TPGridPips": parseFloat(values.v2TpGridPips) || 10.0,
                        "GridManagement.TPMode": values.tpMode || "AverageSpread",
                        "GridManagement.StopLossEnabled": Boolean(values.v2StopLossEnabled),
                        "GridManagement.StopLossPips": parseFloat(values.v2StopLossPips) || 18.0,
                        "GridManagement.BuyGrid": parseFloat(values.buyGrid) || 1.500,
                        "GridManagement.SellGrid": parseFloat(values.sellGrid) || 1.500,
                        "GridManagement.BuyLegTP": parseFloat(values.buyLegTP) || 0.300,
                        "GridManagement.SellLegTP": parseFloat(values.sellLegTP) || 0.300,
                        "GridManagement.BuyLegSL": parseFloat(values.buyLegSL) || 0.500,
                        "GridManagement.SellLegSL": parseFloat(values.sellLegSL) || 0.500,
                        "GridManagement.AccumulativeTP": parseFloat(values.accumulativeTP) || 2.5,
                        "GridManagement.AccumulativeSL": parseFloat(values.accumulativeSL) || -10.0,
                        "RiskManagement.Enabled": Boolean(values.riskManagementEnabled),
                        "SpreadValidation.Enabled": Boolean(values.spreadValidationEnabled),
                        "SpreadValidation.MinAcceptableSpread": parseFloat(values.minAcceptableSpread) || -1000,
                        "SpreadValidation.MaxAcceptableSpread": parseFloat(values.maxAcceptableSpread) || 1000
                    };
                } else {
                    // V1 PARAMETERS - NESTED STRUCTURE
                    parameters = {
                        version: "v1",
                        // Mode
                        mode: values.mode || 'Demo',

                        // Strategy flags (moved to root level)

                        // Grid Management
                        spreadDirection: values.spreadDirection,
                        baseLotSize: parseFloat(values.baseLotSize) || 0.01,
                        minLotSize: parseFloat(values.minLotSize) || 0.01,
                        maxLotOpenedAllowed: parseFloat(values.maxLotOpenedAllowed) || 0.05,
                        gridThreshold: parseFloat(values.gridThreshold) || 2,
                        maxGridLevels: parseInt(values.maxGridLevels) || 3,
                        tpGridPips: parseFloat(values.tpGridPips) || 10,
                        stopLossPips: parseFloat(values.stopLossPips) || 20,
                        tpGridEnabled: Boolean(values.tpGridEnabled),
                        stopLossEnabled: Boolean(values.stopLossEnabled),
                        allowGridReopening: Boolean(values.allowGridReopening),
                        resetGridOnFullClose: Boolean(values.resetGridOnFullClose),

                        // Risk Management
                        riskManagementEnabled: Boolean(values.riskManagementEnabled),
                        warmupMinutes: parseInt(values.warmupMinutes) || 0,
                        maxOrdersPerDay: parseInt(values.maxOrdersPerDay) || 100,
                        orderCooldownMs: parseInt(values.orderCooldownMs) || 220,
                        orderCooldownSeconds: parseInt(values.orderCooldownSeconds) || 0,
                        checkExistingPosition: Boolean(values.checkExistingPosition),
                        maxOpenOrdersPerSide: parseInt(values.maxOpenOrdersPerSide) || 1,
                        maxPositionDurationMinutes: parseInt(values.maxPositionDurationMinutes) || 1440,
                        requireMinimumBars: Boolean(values.requireMinimumBars),
                        logRejectedOrders: Boolean(values.logRejectedOrders),
                        logMetricsEveryNthTick: parseInt(values.logMetricsEveryNthTick) || 100,

                        // Spread Validation
                        minAcceptableSpread: parseFloat(values.minAcceptableSpread) || -1000,
                        maxAcceptableSpread: parseFloat(values.maxAcceptableSpread) || 1000,

                        // EMA
                        emaEnabled: Boolean(values.emaEnabled),
                        emaPeriod: parseInt(values.emaPeriod) || 3,
                        requireBarsForInit: parseInt(values.requireBarsForInit) || 1,
                        medianFilterSize: parseInt(values.medianFilterSize) || 5,

                        // History Management
                        barPeriod: parseInt(values.barPeriod) || 60,
                        barsToKeep: parseInt(values.barsToKeep) || 100,
                        historicalBarsRequired: parseInt(values.historicalBarsRequired) || 3,
                        enableDataCollection: Boolean(values.enableDataCollection),
                        saveHistoricalBarsToDatabase: Boolean(values.saveHistoricalBarsToDatabase),

                        // Logging Configuration
                        loggingEnabled: Boolean(values.loggingEnabled),
                        logLevel: values.logLevel || 'Debug',
                        logEveryNthTick: parseInt(values.logEveryNthTick) || 100,
                        enableCsvLogging: Boolean(values.enableCsvLogging),

                        orderTimeInForce: values.orderTimeInForce || 'GTC',

                        // Open Now settings
                        OpenNow_BuySpread: values.openNow === 'buy',
                        OpenNow_SellSpread: values.openNow === 'sell',

                        // Symbol configuration (no defaults)
                        SpotSymbol: values.spotSymbol || symbolsArray[0] || '',
                        FuturesSymbol: values.futureSymbol || symbolsArray[1] || ''
                    };
                }

                // Calculate symbol changes - only send symbols that are truly new or removed
                const addedSymbols = symbolsArray.filter(symbol => 
                    !originalSymbols.includes(symbol) && 
                    symbol && 
                    symbol.trim() !== ''
                );
                const removedSymbols = originalSymbols.filter(symbol => 
                    !symbolsArray.includes(symbol) && 
                    symbol && 
                    symbol.trim() !== ''
                );
                
                // Additional validation: remove any duplicates and empty values
                const uniqueAddedSymbols = [...new Set(addedSymbols)].filter(s => s && s.trim() !== '');
                const uniqueRemovedSymbols = [...new Set(removedSymbols)].filter(s => s && s.trim() !== '');
                
                // Final validation: ensure no symbol appears in both add and remove arrays
                const finalAddedSymbols = uniqueAddedSymbols.filter(symbol => !uniqueRemovedSymbols.includes(symbol));
                const finalRemovedSymbols = uniqueRemovedSymbols.filter(symbol => !uniqueAddedSymbols.includes(symbol));
                
                console.log('Symbol change tracking:', {
                    originalSymbols,
                    currentSymbols: symbolsArray,
                    finalAddedSymbols,
                    finalRemovedSymbols
                });

                // Prepare data for API call according to the required schema
                const strategyData = {
                    name: values.name,
                    description: values.description || "",
                    parameters: parameters,
                    isEnabled: Boolean(values.isEnabled), // ✅ Root level isEnabled (as per schema)
                    isMultiSymbol: Boolean(values.isMultiSymbol), // ✅ Root level isMultiSymbol (as per schema)
                    addSymbols: finalAddedSymbols.length > 0 ? finalAddedSymbols : [], // Only send if there are changes
                    removeSymbols: finalRemovedSymbols.length > 0 ? finalRemovedSymbols : [], // Only send if there are changes
                    lastModifiedBy: lastModifiedBy,
                    accountNumber: selectedAccount ? parseInt(selectedAccount) : (strategy?.accountNumber || null)
                };

                // Call API
                const result = await updateStrategy(token, strategy.id, strategyData);
                
                // Success
                    toast.success(`Strategy "${values.name}" updated successfully!`);
                onSuccess && onSuccess(result);
                onClose();
                
            } catch (error) {
                
                // Handle specific error messages from the API
                let errorMessage = 'Failed to update strategy';
                
                if (error.message) {
                    errorMessage = error.message;
                } else if (error.error) {
                    errorMessage = error.error;
                } else if (typeof error === 'string') {
                    errorMessage = error;
                }
                
                // Check for specific error types
                if (errorMessage.includes('already exists')) {
                    errorMessage = `❌ ${errorMessage}\n\n💡 Please choose a different strategy name.`;
                } else if (errorMessage.includes('validation')) {
                    errorMessage = `❌ Validation Error: ${errorMessage}\n\n💡 Please check all required fields and try again.`;
                } else if (errorMessage.includes('missing')) {
                    errorMessage = `❌ ${errorMessage}\n\n💡 Please refresh the page and try again.`;
                }
                
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        }
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open && strategy) {
            setActiveTab(0); // Always show first tab (Common) when modal opens
            console.log('EditStrategyModal - Strategy object:', strategy);
            console.log('EditStrategyModal - Strategy parameters:', strategy.parameters);
            console.log('EditStrategyModal - isMultiSymbol:', strategy.isMultiSymbol, strategy.parameters?.isMultiSymbol);
            console.log('EditStrategyModal - isEnabled:', strategy.isEnabled, strategy.parameters?.Enabled, strategy.parameters?.isEnabled);
            console.log('EditStrategyModal - Final isEnabled value:', strategy.isEnabled || strategy.parameters?.Enabled || strategy.parameters?.isEnabled || true);
            
            // Debug: Check all possible locations for isEnabled
            console.log('EditStrategyModal - All isEnabled locations:', {
                'strategy.isEnabled': strategy.isEnabled,
                'strategy.parameters.Enabled': strategy.parameters?.Enabled,
                'strategy.parameters.isEnabled': strategy.parameters?.isEnabled,
                'strategy.parameters.enabled': strategy.parameters?.enabled,
                'strategy.enabled': strategy.enabled
            });
            
            // Populate symbols from strategy data - extract symbol strings from objects
            const strategySymbols = strategy.symbols || [];
            const symbolStrings = strategySymbols.map(symbolObj => symbolObj.symbol || symbolObj);
            setSymbols(symbolStrings);
            setOriginalSymbols(symbolStrings); // Track original symbols for comparison
            
            // Fetch active symbols for dropdown
            fetchActiveSymbols();
            
            const finalIsEnabled = strategy.isEnabled !== undefined ? strategy.isEnabled : 
                                   strategy.parameters?.Enabled !== undefined ? strategy.parameters.Enabled :
                                   strategy.parameters?.isEnabled !== undefined ? strategy.parameters.isEnabled :
                                   strategy.parameters?.enabled !== undefined ? strategy.parameters.enabled :
                                   strategy.enabled !== undefined ? strategy.enabled : true;
            console.log('EditStrategyModal - Setting isEnabled to:', finalIsEnabled);
            
            formik.resetForm({
                values: {
                    name: strategy.name || '',
                    description: strategy.description || '',
                    strategyType: strategy.strategyType || 'goldarb',
                    mode: strategy.parameters?.Mode || 'Demo',
                    isMultiSymbol: strategy.isMultiSymbol !== undefined ? strategy.isMultiSymbol :
                                   strategy.parameters?.isMultiSymbol !== undefined ? strategy.parameters.isMultiSymbol : false,
                    isEnabled: finalIsEnabled,
                    // Open Now settings
                    openNow: strategy.parameters?.OpenNow_BuySpread ? 'buy' : 
                             strategy.parameters?.OpenNow_SellSpread ? 'sell' : 'none',
                    // Grid Management parameters - using flat structure from database
                    spreadDirection: strategy.parameters?.spreadDirection || strategy.parameters?.GridManagement?.SpreadDirection || 'Buy Spread',
                    baseLotSize: strategy.parameters?.baseLotSize?.toString() || strategy.parameters?.GridManagement?.BaseLotSize?.toString() || '0.01',
                    minLotSize: strategy.parameters?.minLotSize?.toString() || strategy.parameters?.GridManagement?.MinLotSize?.toString() || '0.01',
                    maxLotOpenedAllowed: strategy.parameters?.maxLotOpenedAllowed?.toString() || strategy.parameters?.GridManagement?.MaxLotOpenedAllowed?.toString() || '0.05',
                    gridThreshold: strategy.parameters?.gridThreshold?.toString() || strategy.parameters?.GridManagement?.GridThreshold?.toString() || '2',
                    maxGridLevels: strategy.parameters?.maxGridLevels?.toString() || strategy.parameters?.GridManagement?.MaxGridLevels?.toString() || '3',
                    tpGridPips: strategy.parameters?.tpGridPips?.toString() || strategy.parameters?.GridManagement?.TPGridPips?.toString() || '10',
                    stopLossPips: strategy.parameters?.stopLossPips?.toString() || strategy.parameters?.GridManagement?.StopLossPips?.toString() || '20',
                    tpGridEnabled: strategy.parameters?.tpGridEnabled || strategy.parameters?.GridManagement?.TPGridEnabled || false,
                    stopLossEnabled: strategy.parameters?.stopLossEnabled || strategy.parameters?.GridManagement?.StopLossEnabled || false,
                    allowGridReopening: strategy.parameters?.allowGridReopening || strategy.parameters?.GridManagement?.AllowGridReopening || false,
                    resetGridOnFullClose: strategy.parameters?.resetGridOnFullClose || strategy.parameters?.GridManagement?.ResetGridOnFullClose || false,
                    // Risk Management parameters - using flat structure from database
                    riskManagementEnabled: strategy.parameters?.riskManagementEnabled || strategy.parameters?.RiskManagement?.Enabled || false,
                    warmupMinutes: strategy.parameters?.warmupMinutes?.toString() || strategy.parameters?.RiskManagement?.WarmupMinutes?.toString() || '0',
                    maxOrdersPerDay: strategy.parameters?.maxOrdersPerDay?.toString() || strategy.parameters?.RiskManagement?.MaxOrdersPerDay?.toString() || '100',
                    orderCooldownMs: strategy.parameters?.orderCooldownMs?.toString() || strategy.parameters?.RiskManagement?.OrderCooldownMs?.toString() || '220',
                    orderCooldownSeconds: strategy.parameters?.orderCooldownSeconds?.toString() || strategy.parameters?.RiskManagement?.OrderCooldownSeconds?.toString() || '0',
                    checkExistingPosition: strategy.parameters?.checkExistingPosition || strategy.parameters?.RiskManagement?.CheckExistingPosition || true,
                    maxOpenOrdersPerSide: strategy.parameters?.maxOpenOrdersPerSide?.toString() || strategy.parameters?.RiskManagement?.MaxOpenOrdersPerSide?.toString() || '1',
                    maxPositionDurationMinutes: strategy.parameters?.maxPositionDurationMinutes?.toString() || strategy.parameters?.RiskManagement?.MaxPositionDurationMinutes?.toString() || '1440',
                    requireMinimumBars: strategy.parameters?.requireMinimumBars || strategy.parameters?.RiskManagement?.RequireMinimumBars || false,
                    logRejectedOrders: strategy.parameters?.logRejectedOrders || strategy.parameters?.RiskManagement?.LogRejectedOrders || true,
                    logMetricsEveryNthTick: strategy.parameters?.logMetricsEveryNthTick?.toString() || strategy.parameters?.RiskManagement?.LogMetricsEveryNthTick?.toString() || '100',
                    // Spread Validation parameters - using flat structure from database
                    minAcceptableSpread: strategy.parameters?.minAcceptableSpread?.toString() || strategy.parameters?.SpreadValidation?.MinAcceptableSpread?.toString() || '-1000',
                    maxAcceptableSpread: strategy.parameters?.maxAcceptableSpread?.toString() || strategy.parameters?.SpreadValidation?.MaxAcceptableSpread?.toString() || '1000',
                    // EMA parameters - using flat structure from database
                    emaEnabled: strategy.parameters?.emaEnabled || strategy.parameters?.EMA?.Enabled || false,
                    emaPeriod: strategy.parameters?.emaPeriod?.toString() || strategy.parameters?.EMA?.Period?.toString() || '3',
                    requireBarsForInit: strategy.parameters?.requireBarsForInit?.toString() || strategy.parameters?.RequireBarsForInit?.toString() || '1',
                    medianFilterSize: strategy.parameters?.medianFilterSize?.toString() || strategy.parameters?.MedianFilterSize?.toString() || '5',
                    // History Management parameters - using flat structure from database
                    barPeriod: strategy.parameters?.barPeriod?.toString() || strategy.parameters?.History?.BarPeriod?.toString() || '60',
                    barsToKeep: strategy.parameters?.barsToKeep?.toString() || strategy.parameters?.History?.BarsToKeep?.toString() || '100',
                    enableDataCollection: strategy.parameters?.enableDataCollection || strategy.parameters?.History?.EnableDataCollection || false,
                    saveHistoricalBarsToDatabase: strategy.parameters?.saveHistoricalBarsToDatabase || strategy.parameters?.History?.SaveHistoricalBarsToDatabase || false,
                    // Logging Configuration parameters - using flat structure from database
                    loggingEnabled: strategy.parameters?.loggingEnabled || strategy.parameters?.Logging?.Enabled || false,
                    logLevel: strategy.parameters?.logLevel || strategy.parameters?.Logging?.LogLevel || 'Debug',
                    logEveryNthTick: strategy.parameters?.logEveryNthTick?.toString() || strategy.parameters?.Logging?.LogEveryNthTick?.toString() || '100',
                    orderTimeInForce: strategy.parameters?.orderTimeInForce || strategy.parameters?.OrderTimeInForce || 'GTC',
                    // Additional fields from database
                    useEma: strategy.parameters?.UseEMA || false,
                    lotSizeMultiplier: strategy.parameters?.GridManagement?.LotSizeMultiplier?.toString() || '1.5',
                    requireMinimumBars: strategy.parameters?.RiskManagement?.RequireMinimumBars || false,
                    openNowBuySpread: strategy.parameters?.OpenNow_BuySpread || false,
                    openNowSellSpread: strategy.parameters?.OpenNow_SellSpread || false,
                    // EMA additional fields
                    emaMinBarsRequired: strategy.parameters?.EMA?.MinBarsRequired?.toString() || '1',
                    emaBarTimeframeMinutes: strategy.parameters?.EMA?.BarTimeframeMinutes?.toString() || '1',
                    // V2-specific fields
                    bidirectionalMode: strategy.parameters?.['GridManagement.BidirectionalMode'] || false,
                    maxTotalLotBothDirections: strategy.parameters?.['GridManagement.MaxTotalLotBothDirections']?.toString() || '0.30',
                    useBenchmarkMode: strategy.parameters?.['GridManagement.UseBenchmarkMode'] || false,
                    benchmarkThresholdBuy: strategy.parameters?.['GridManagement.BenchmarkThresholdBuy']?.toString() || '4.0',
                    benchmarkThresholdSell: strategy.parameters?.['GridManagement.BenchmarkThresholdSell']?.toString() || '4.0',
                    v2GridThreshold: strategy.parameters?.['GridManagement.GridThreshold']?.toString() || '2.0',
                    v2MaxGridLevels: strategy.parameters?.['GridManagement.MaxGridLevels']?.toString() || '5',
                    buyBaseLotSize: strategy.parameters?.['GridManagement.BuyBaseLotSize']?.toString() || '0.01',
                    sellBaseLotSize: strategy.parameters?.['GridManagement.SellBaseLotSize']?.toString() || '0.01',
                    buyLotSizeMultiplier: strategy.parameters?.['GridManagement.BuyLotSizeMultiplier']?.toString() || '1.2',
                    sellLotSizeMultiplier: strategy.parameters?.['GridManagement.SellLotSizeMultiplier']?.toString() || '1.2',
                    buyMaxLotOpenedAllowed: strategy.parameters?.['GridManagement.BuyMaxLotOpenedAllowed']?.toString() || '0.15',
                    sellMaxLotOpenedAllowed: strategy.parameters?.['GridManagement.SellMaxLotOpenedAllowed']?.toString() || '0.15',
                    v2TpGridEnabled: strategy.parameters?.['GridManagement.TPGridEnabled'] || false,
                    v2TpGridPips: strategy.parameters?.['GridManagement.TPGridPips']?.toString() || '10.0',
                    tpMode: strategy.parameters?.['GridManagement.TPMode'] || 'AverageSpread',
                    v2StopLossEnabled: strategy.parameters?.['GridManagement.StopLossEnabled'] || false,
                    v2StopLossPips: strategy.parameters?.['GridManagement.StopLossPips']?.toString() || '18.0',
                    spreadValidationEnabled: strategy.parameters?.['SpreadValidation.Enabled'] || strategy.parameters?.spreadValidationEnabled || false,
                    buyGrid: strategy.parameters?.['GridManagement.BuyGrid']?.toString() || '1.500',
                    sellGrid: strategy.parameters?.['GridManagement.SellGrid']?.toString() || '1.500',
                    buyLegTP: strategy.parameters?.['GridManagement.BuyLegTP']?.toString() || '0.300',
                    sellLegTP: strategy.parameters?.['GridManagement.SellLegTP']?.toString() || '0.300',
                    buyLegSL: strategy.parameters?.['GridManagement.BuyLegSL']?.toString() || '0.500',
                    sellLegSL: strategy.parameters?.['GridManagement.SellLegSL']?.toString() || '0.500',
                    accumulativeTP: strategy.parameters?.['GridManagement.AccumulativeTP']?.toString() || '2.5',
                    accumulativeSL: strategy.parameters?.['GridManagement.AccumulativeSL']?.toString() || '-10.0',
                    spotSymbol: strategy.parameters?.SpotSymbol || '',
                    futureSymbol: strategy.parameters?.FuturesSymbol || ''
                }
            });
            setError(null);
        }
    }, [open, strategy]);

    // Update symbol fields when symbols are loaded
    useEffect(() => {
        if (symbols.length > 0) {
            // Only update if the current values are empty or if they don't match the strategy values
            const currentSpotSymbol = formik.values.spotSymbol;
            const currentFutureSymbol = formik.values.futureSymbol;
            
            if (!currentSpotSymbol || currentSpotSymbol === '') {
                formik.setFieldValue('spotSymbol', symbols[0]);
            }
            if (!currentFutureSymbol || currentFutureSymbol === '') {
                formik.setFieldValue('futureSymbol', symbols.length > 1 ? symbols[1] : symbols[0]);
            }
        }
    }, [symbols]);

    const handleClose = () => {
        if (!loading) {
            formik.resetForm();
            setError(null);
            setSymbols([]);
            setOriginalSymbols([]); // Reset original symbols
            setNewSymbol('');
            onClose();
        }
    };

    const handleAddSymbol = () => {
        const trimmedSymbol = newSymbol.trim();
        if (trimmedSymbol && !symbols.includes(trimmedSymbol)) {
            setSymbols([...symbols, trimmedSymbol]);
            setNewSymbol('');
        }
    };

    const handleRemoveSymbol = (symbolToRemove) => {
        setSymbols(symbols.filter(symbol => symbol !== symbolToRemove));
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddSymbol();
        }
    };

    // Fetch active symbols from database
    const fetchActiveSymbols = async () => {
        try {
            setSymbolsLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Authentication token not found');
            }
            
            console.log('Fetching active symbols...');

            // Add timeout wrapper to prevent hanging
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Request timeout')), 8000); // 8 second timeout
            });

            const apiPromise = getActiveSymbols(token);
            
            const response = await Promise.race([apiPromise, timeoutPromise]);
            
            console.log('Active symbols API response:', response);
            
            // Handle API response structure
            const symbolsData = Array.isArray(response) ? response : (response?.data || []);
            
            console.log('Processed symbols data:', symbolsData);
            
            // Extract symbol names and filter for active symbols
            const symbolNames = symbolsData
                .filter(symbol => symbol.isActive === 1 || symbol.isActive === true)
                .map(symbol => symbol.symbol || symbol.name || symbol.symbolName)
                .filter(name => name); // Remove any null/undefined names
            
            console.log('Final symbol names:', symbolNames);
            setActiveSymbols(symbolNames);
            
        } catch (error) {
            if (error.message === 'Request timeout') {
                toast.warning('Symbols loading is taking longer than expected. You can still add custom symbols.');
            } else {
                toast.error('Failed to load symbols from database');
            }
            // Don't set activeSymbols to empty array, let user still type custom symbols
        } finally {
            setSymbolsLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="edit-strategy-modal-title"
            aria-describedby="edit-strategy-modal-description"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
                maxWidth: 1200,
                maxHeight: '90vh',
                bgcolor: 'rgba(24, 31, 44, 0.95)',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EditIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                        <Box>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                                Edit Strategy
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                Update strategy information and settings
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={handleClose} 
                        disabled={loading}
                        sx={{ color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'rgba(6, 182, 212, 0.2)' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        sx={{
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#ff9800',
                                height: 3
                            },
                            '& .MuiTab-root': {
                                color: '#bfc9db',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '1rem',
                                '&.Mui-selected': {
                                    color: '#ff9800'
                                }
                            }
                        }}
                    >
                        <Tab label="Common" />
                        <Tab label="Inputs" />
                    </Tabs>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 3,
                                background: 'rgba(211, 47, 47, 0.1)',
                                border: '1px solid #d32f2f',
                                color: '#fff',
                                '& .MuiAlert-icon': { color: '#ff6b6b' },
                                '& .MuiAlert-message': { 
                                    whiteSpace: 'pre-line',
                                    fontSize: '0.9rem'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={formik.handleSubmit}>
                        {activeTab === 0 && (
                            <Box>
                                {/* Strategy Information Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="body1" sx={{ color: '#fff', minWidth: 120, mr: 2 }}>
                                            Strategy Name:
                                </Typography>
                                <TextField
                                    fullWidth
                                    id="name"
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name ? formik.errors.name : 'Strategy name cannot be changed (from template)'}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                            color: '#fff',
                                            cursor: 'not-allowed',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#ff9800' }
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: formik.touched.name && formik.errors.name ? '#ff6b6b' : 'rgba(255, 255, 255, 0.5)'
                                        }
                                    }}
                                />
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="body1" sx={{ color: '#fff', minWidth: 120, mr: 2 }}>
                                            Strategy Type:
                                        </Typography>
                                        <FormControl 
                                            fullWidth
                                            error={formik.touched.strategyType && Boolean(formik.errors.strategyType)}
                                        >
                                    <Select
                                        id="strategyType"
                                        name="strategyType"
                                        value={formik.values.strategyType}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        sx={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            color: '#fff',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff9800' },
                                            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b6b' },
                                            '& .MuiSvgIcon-root': { color: '#bfc9db' }
                                        }}
                                    >
                                        {strategyTypes.map((type) => (
                                            <MenuItem key={type} value={type}>{type}</MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.strategyType && formik.errors.strategyType && (
                                        <Typography variant="caption" sx={{ color: '#ff6b6b', mt: 0.5, ml: 2 }}>
                                            {formik.errors.strategyType}
                                        </Typography>
                                    )}
                                </FormControl>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                        <Typography variant="body1" sx={{ color: '#fff', minWidth: 120, mr: 2, mt: 1 }}>
                                            Description:
                                        </Typography>
                                <TextField
                                    fullWidth
                                    id="description"
                                    name="description"
                                    multiline
                                    rows={3}
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.description && Boolean(formik.errors.description)}
                                    helperText={formik.touched.description && formik.errors.description}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            color: '#fff',
                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                            '&.Mui-focused fieldset': { borderColor: '#ff9800' }
                                        },
                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                    }}
                                />
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="body1" sx={{ color: '#fff', minWidth: 120, mr: 2 }}>
                                            Is Multi Symbol:
                                        </Typography>
                                        <Checkbox
                                            name="isMultiSymbol"
                                            checked={Boolean(formik.values.isMultiSymbol)}
                                            onChange={(e) => {
                                                formik.setFieldValue('isMultiSymbol', e.target.checked);
                                            }}
                                            sx={{
                                                color: '#06b6d4',
                                                '&.Mui-checked': {
                                                    color: '#06b6d4',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', my: 2 }} />

                                {/* Dynamic Symbol Management Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 20 }} />
                                        Symbol Management
                                        </Typography>
                                    
                                    {/* Add Symbol Autocomplete */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Autocomplete
                                            fullWidth
                                            options={activeSymbols}
                                            value={newSymbol}
                                            onChange={(event, newValue) => {
                                                setNewSymbol(newValue || '');
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select symbol from dropdown"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#ff9800' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiInputBase-input': { color: '#fff' }
                                                    }}
                                                />
                                            )}
                                            filterOptions={(options, { inputValue }) => {
                                                return options.filter(option =>
                                                    option.toLowerCase().includes(inputValue.toLowerCase())
                                                );
                                            }}
                                            renderOption={(props, option) => (
                                                <Box
                                                    {...props}
                                                    sx={{
                                                        color: symbols.includes(option) ? '#666' : '#fff',
                                                        backgroundColor: symbols.includes(option) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: symbols.includes(option) ? 'rgba(255, 255, 255, 0.1)' : 'rgba(6, 182, 212, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    {option} {symbols.includes(option) && '(Already added)'}
                                                </Box>
                                            )}
                                            sx={{
                                                '& .MuiAutocomplete-popupIndicator': {
                                                    color: '#bfc9db'
                                                },
                                                '& .MuiAutocomplete-clearIndicator': {
                                                    color: '#bfc9db'
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleAddSymbol}
                                            disabled={!newSymbol.trim() || symbols.includes(newSymbol.trim())}
                                            startIcon={<AddIcon />}
                                            sx={{
                                                background: '#06b6d4',
                                                color: '#fff',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                '&:hover': { background: '#0891b2' },
                                                '&:disabled': { background: 'rgba(6, 182, 212, 0.3)' }
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </Box>

                                    {/* Display Added Symbols */}
                                    {symbols.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" sx={{ color: '#bfc9db', mb: 1 }}>
                                                Added Symbols ({symbols.length}):
                                        </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {symbols.map((symbol, index) => (
                                                    <Chip
                                                        key={index}
                                                        label={symbol}
                                                        onDelete={() => handleRemoveSymbol(symbol)}
                                                        deleteIcon={<DeleteIcon />}
                                                        sx={{
                                                            backgroundColor: 'rgba(6, 182, 212, 0.2)',
                                                            color: '#fff',
                                                            border: '1px solid rgba(6, 182, 212, 0.3)',
                                                            '& .MuiChip-deleteIcon': {
                                                                color: '#ff6b6b',
                                                                '&:hover': { color: '#ff4444' }
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(6, 182, 212, 0.3)'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>

                                {/* Help Text */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1, 
                                    mt: 2, 
                                    p: 2, 
                                    background: 'rgba(255, 152, 0, 0.1)', 
                                    borderRadius: 1,
                                    border: '1px solid rgba(255, 152, 0, 0.3)'
                                }}>
                                    <InfoIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                    <Typography variant="caption" sx={{ color: '#bfc9db' }}>
                                        Configure basic strategy information and symbol selection
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {activeTab === 1 && (
                            <Box>
                                {/* V2 FORM - Flat Structure */}
                                {formVersion === 'v2' && (
                                    <>
                                        {/* Strategy Settings Section - V2 */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <InfoIcon sx={{ fontSize: 20 }} />
                                                Strategy Settings
                                            </Typography>

                                            {/* Table-like layout */}
                                            <Box sx={{
                                                background: 'rgba(255, 255, 255, 0.02)',
                                                borderRadius: 2,
                                                border: '1px solid rgba(6, 182, 212, 0.2)',
                                                overflow: 'hidden'
                                            }}>
                                                {/* Table Header */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    background: 'rgba(6, 182, 212, 0.1)',
                                                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)'
                                                }}>
                                                    <Box sx={{ flex: 1, p: 2, borderRight: '1px solid rgba(6, 182, 212, 0.2)' }}>
                                                        <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                            Variable
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                            Value
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Enabled */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Enabled
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Checkbox
                                                            name="isEnabled"
                                                            checked={Boolean(formik.values.isEnabled)}
                                                            onChange={(e) => formik.setFieldValue('isEnabled', e.target.checked)}
                                                            sx={{
                                                                color: '#bfc9db',
                                                                '&.Mui-checked': { color: '#1de9b6' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Spot Symbol */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Spot Symbol
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                name="spotSymbol"
                                                                value={formik.values.spotSymbol}
                                                                onChange={formik.handleChange}
                                                                displayEmpty
                                                                sx={{
                                                                    color: '#fff',
                                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1de9b6' },
                                                                    '.MuiSvgIcon-root': { color: '#bfc9db' }
                                                                }}
                                                            >
                                                                <MenuItem value="">Select Spot Symbol</MenuItem>
                                                                {symbols.map((symbol, index) => (
                                                                    <MenuItem key={index} value={symbol}>
                                                                        {symbol}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                </Box>

                                                {/* Future Symbol */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Future Symbol
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                name="futureSymbol"
                                                                value={formik.values.futureSymbol}
                                                                onChange={formik.handleChange}
                                                                displayEmpty
                                                                sx={{
                                                                    color: '#fff',
                                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1de9b6' },
                                                                    '.MuiSvgIcon-root': { color: '#bfc9db' }
                                                                }}
                                                            >
                                                                <MenuItem value="">Select Future Symbol</MenuItem>
                                                                {symbols.map((symbol, index) => (
                                                                    <MenuItem key={index} value={symbol}>
                                                                        {symbol}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Grid Management Section - V2 */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <InfoIcon sx={{ fontSize: 20 }} />
                                                Grid Management
                                            </Typography>

                                            {/* Table-like layout */}
                                            <Box sx={{
                                                background: 'rgba(255, 255, 255, 0.02)',
                                                borderRadius: 2,
                                                border: '1px solid rgba(6, 182, 212, 0.2)',
                                                overflow: 'hidden'
                                            }}>
                                                {/* Table Header */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    background: 'rgba(6, 182, 212, 0.1)',
                                                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)'
                                                }}>
                                                    <Box sx={{ flex: 1, p: 2, borderRight: '1px solid rgba(6, 182, 212, 0.2)' }}>
                                                        <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                            Variable
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                            Value
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Table Rows - V2 Fields */}
                                                {/* Bidirectional Mode */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Bidirectional Mode
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Checkbox
                                                            name="bidirectionalMode"
                                                            checked={Boolean(formik.values.bidirectionalMode)}
                                                            onChange={(e) => formik.setFieldValue('bidirectionalMode', e.target.checked)}
                                                            sx={{
                                                                color: '#bfc9db',
                                                                '&.Mui-checked': { color: '#1de9b6' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Max Total Lot Both Directions */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Max Total Lot Both Directions
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="maxTotalLotBothDirections"
                                                            value={formik.values.maxTotalLotBothDirections}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Use Benchmark Mode */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Use Benchmark Mode
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Checkbox
                                                            name="useBenchmarkMode"
                                                            checked={Boolean(formik.values.useBenchmarkMode)}
                                                            onChange={(e) => formik.setFieldValue('useBenchmarkMode', e.target.checked)}
                                                            sx={{
                                                                color: '#bfc9db',
                                                                '&.Mui-checked': { color: '#1de9b6' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Benchmark Threshold Buy */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Benchmark Threshold Buy
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="benchmarkThresholdBuy"
                                                            value={formik.values.benchmarkThresholdBuy}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Benchmark Threshold Sell */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#2196f3' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Benchmark Threshold Sell
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="benchmarkThresholdSell"
                                                            value={formik.values.benchmarkThresholdSell}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Grid Threshold */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Grid Threshold
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="v2GridThreshold"
                                                            value={formik.values.v2GridThreshold}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Max Grid Levels */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Max Grid Levels
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="v2MaxGridLevels"
                                                            value={formik.values.v2MaxGridLevels}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Buy Base Lot Size */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Buy Base Lot Size
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="buyBaseLotSize"
                                                            value={formik.values.buyBaseLotSize}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Sell Base Lot Size */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Sell Base Lot Size
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="sellBaseLotSize"
                                                            value={formik.values.sellBaseLotSize}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Buy Lot Size Multiplier */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Buy Lot Size Multiplier
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="buyLotSizeMultiplier"
                                                            value={formik.values.buyLotSizeMultiplier}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Sell Lot Size Multiplier */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Sell Lot Size Multiplier
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="sellLotSizeMultiplier"
                                                            value={formik.values.sellLotSizeMultiplier}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Max Lot Opened Allowed */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Max Lot Opened Allowed
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="v2MaxLotOpenedAllowed"
                                                            value={formik.values.v2MaxLotOpenedAllowed}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* TP Grid Enabled */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            TP Grid Enabled
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Checkbox
                                                            name="v2TpGridEnabled"
                                                            checked={Boolean(formik.values.v2TpGridEnabled)}
                                                            onChange={(e) => formik.setFieldValue('v2TpGridEnabled', e.target.checked)}
                                                            sx={{
                                                                color: '#bfc9db',
                                                                '&.Mui-checked': { color: '#1de9b6' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* TP Grid Pips */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            TP Grid Pips
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="v2TpGridPips"
                                                            value={formik.values.v2TpGridPips}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* TP Mode */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#2196f3' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            TP Mode
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <FormControl fullWidth size="small">
                                                            <Select
                                                                name="tpMode"
                                                                value={formik.values.tpMode}
                                                                onChange={formik.handleChange}
                                                                sx={{
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#06b6d4' },
                                                                    '& .MuiSvgIcon-root': { color: '#bfc9db' }
                                                                }}
                                                            >
                                                                <MenuItem value="AverageSpread">Average Spread</MenuItem>
                                                                <MenuItem value="FixedPips">Fixed Pips</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                </Box>

                                                {/* Stop Loss Enabled */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Stop Loss Enabled
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Checkbox
                                                            name="v2StopLossEnabled"
                                                            checked={Boolean(formik.values.v2StopLossEnabled)}
                                                            onChange={(e) => formik.setFieldValue('v2StopLossEnabled', e.target.checked)}
                                                            sx={{
                                                                color: '#bfc9db',
                                                                '&.Mui-checked': { color: '#1de9b6' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Stop Loss Pips */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Stop Loss Pips
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="v2StopLossPips"
                                                            value={formik.values.v2StopLossPips}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Buy Grid */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Buy Grid
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            name="buyGrid"
                                                            value={formik.values.buyGrid}
                                                            onChange={formik.handleChange}
                                                            inputProps={{ step: "0.001" }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Sell Grid */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#f44336' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Sell Grid
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            name="sellGrid"
                                                            value={formik.values.sellGrid}
                                                            onChange={formik.handleChange}
                                                            inputProps={{ step: "0.001" }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Buy Leg TP */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#2196f3' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Buy Leg TP
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            name="buyLegTP"
                                                            value={formik.values.buyLegTP}
                                                            onChange={formik.handleChange}
                                                            inputProps={{ step: "0.001" }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Sell Leg TP */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Sell Leg TP
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            name="sellLegTP"
                                                            value={formik.values.sellLegTP}
                                                            onChange={formik.handleChange}
                                                            inputProps={{ step: "0.001" }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Buy Leg SL */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#f44336' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            BuyLegSL
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            name="buyLegSL"
                                                            value={formik.values.buyLegSL}
                                                            onChange={formik.handleChange}
                                                            inputProps={{ step: "0.001" }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Sell Leg SL */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#ff5722' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            SellLegSL
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            name="sellLegSL"
                                                            value={formik.values.sellLegSL}
                                                            onChange={formik.handleChange}
                                                            inputProps={{ step: "0.001" }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Accumulative TP */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#00bcd4' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Accumulative TP
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            name="accumulativeTP"
                                                            value={formik.values.accumulativeTP}
                                                            onChange={formik.handleChange}
                                                            inputProps={{ step: "0.1" }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Accumulative SL */}
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Accumulative SL
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            name="accumulativeSL"
                                                            value={formik.values.accumulativeSL}
                                                            onChange={formik.handleChange}
                                                            inputProps={{ step: "0.1" }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    color: '#fff',
                                                                    '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                                    '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                                    '&.Mui-focused fieldset': { borderColor: '#06b6d4' }
                                                                },
                                                                '& .MuiOutlinedInput-input': { color: '#fff' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Risk Management - V2 */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <InfoIcon sx={{ fontSize: 20 }} />
                                                Risk Management
                                            </Typography>
                                            <Box sx={{
                                                background: 'rgba(255, 255, 255, 0.02)',
                                                borderRadius: 2,
                                                border: '1px solid rgba(6, 182, 212, 0.2)',
                                                overflow: 'hidden'
                                            }}>
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Enabled
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Checkbox
                                                            name="riskManagementEnabled"
                                                            checked={Boolean(formik.values.riskManagementEnabled)}
                                                            onChange={(e) => formik.setFieldValue('riskManagementEnabled', e.target.checked)}
                                                            sx={{
                                                                color: '#bfc9db',
                                                                '&.Mui-checked': { color: '#1de9b6' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Spread Validation - V2 */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <InfoIcon sx={{ fontSize: 20 }} />
                                                Spread Validation
                                            </Typography>
                                            <Box sx={{
                                                background: 'rgba(255, 255, 255, 0.02)',
                                                borderRadius: 2,
                                                border: '1px solid rgba(6, 182, 212, 0.2)',
                                                overflow: 'hidden'
                                            }}>
                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            Enabled
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <Checkbox
                                                            name="spreadValidationEnabled"
                                                            checked={Boolean(formik.values.spreadValidationEnabled)}
                                                            onChange={(e) => formik.setFieldValue('spreadValidationEnabled', e.target.checked)}
                                                            sx={{
                                                                color: '#bfc9db',
                                                                '&.Mui-checked': { color: '#1de9b6' }
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            MinAcceptableSpread
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="minAcceptableSpread"
                                                            value={formik.values.minAcceptableSpread}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    '& fieldset': {
                                                                        borderColor: 'rgba(6, 182, 212, 0.3)',
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: 'rgba(6, 182, 212, 0.5)',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#06b6d4',
                                                                    },
                                                                },
                                                                '& .MuiOutlinedInput-input': {
                                                                    color: '#fff',
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                                    <Box sx={{
                                                        flex: 1,
                                                        p: 2,
                                                        borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                        <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            MaxAcceptableSpread
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ flex: 1, p: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            name="maxAcceptableSpread"
                                                            value={formik.values.maxAcceptableSpread}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                                    '& fieldset': {
                                                                        borderColor: 'rgba(6, 182, 212, 0.3)',
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: 'rgba(6, 182, 212, 0.5)',
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#06b6d4',
                                                                    },
                                                                },
                                                                '& .MuiOutlinedInput-input': {
                                                                    color: '#fff',
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </>
                                )}

                                {/* V1 FORM - Nested Structure */}
                                {formVersion === 'v1' && (
                                    <>
                                        {/* Grid Management Section - V1 */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <InfoIcon sx={{ fontSize: 20 }} />
                                                Grid Management
                                            </Typography>

                                            {/* Table-like layout */}
                                    <Box sx={{ 
                                        background: 'rgba(255, 255, 255, 0.02)', 
                                        borderRadius: 2, 
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Table Header */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            background: 'rgba(6, 182, 212, 0.1)',
                                            borderBottom: '1px solid rgba(6, 182, 212, 0.2)'
                                        }}>
                                            <Box sx={{ flex: 1, p: 2, borderRight: '1px solid rgba(6, 182, 212, 0.2)' }}>
                                                <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                    Variable
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                    Value
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Table Rows */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    SpreadDirection
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={formik.values.spreadDirection}
                                                        onChange={formik.handleChange}
                                                        name="spreadDirection"
                                                        sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="Buy Spread">Buy Spread</MenuItem>
                                                        <MenuItem value="Sell Spread">Sell Spread</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    BaseLotSize
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="baseLotSize"
                                                    value={formik.values.baseLotSize}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MinLotSize
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="minLotSize"
                                                    value={formik.values.minLotSize}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MaxLotOpenedAllowed
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="maxLotOpenedAllowed"
                                                    value={formik.values.maxLotOpenedAllowed}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    GridThreshold
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="gridThreshold"
                                                    value={formik.values.gridThreshold}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MaxGridLevels
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="maxGridLevels"
                                                    value={formik.values.maxGridLevels}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    TPGridPips
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="tpGridPips"
                                                    value={formik.values.tpGridPips}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#f44336' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    StopLossPips
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="stopLossPips"
                                                    value={formik.values.stopLossPips}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    TPGridEnabled
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                        <Checkbox
                                                    name="tpGridEnabled"
                                                    checked={Boolean(formik.values.tpGridEnabled)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('tpGridEnabled', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    StopLossEnabled
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="stopLossEnabled"
                                                    checked={Boolean(formik.values.stopLossEnabled)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('stopLossEnabled', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    AllowGridReopening
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="allowGridReopening"
                                                    checked={Boolean(formik.values.allowGridReopening)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('allowGridReopening', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    ResetGridOnFullClose
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="resetGridOnFullClose"
                                                    checked={Boolean(formik.values.resetGridOnFullClose)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('resetGridOnFullClose', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Symbols Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                                        Core Settings
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        background: 'rgba(255, 255, 255, 0.02)', 
                                        borderRadius: 2, 
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Table Header */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            background: 'rgba(6, 182, 212, 0.1)',
                                            borderBottom: '1px solid rgba(6, 182, 212, 0.2)'
                                        }}>
                                            <Box sx={{ flex: 1, p: 2, borderRight: '1px solid rgba(6, 182, 212, 0.2)' }}>
                                                <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                    Variable
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                    Value
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* SpotSymbol Row */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    SpotSymbol
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={formik.values.spotSymbol}
                                                        onChange={formik.handleChange}
                                                        name="spotSymbol"
                                                        displayEmpty
                                                        sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="">Select Spot Symbol</MenuItem>
                                                        {symbols.map((symbol, index) => (
                                                            <MenuItem key={index} value={symbol}>
                                                                {symbol}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>

                                        {/* FutureSymbol Row */}
                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    FutureSymbol
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={formik.values.futureSymbol}
                                                        onChange={formik.handleChange}
                                                        name="futureSymbol"
                                                        displayEmpty
                                                        sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="">Select Future Symbol</MenuItem>
                                                        {symbols.map((symbol, index) => (
                                                            <MenuItem key={index} value={symbol}>
                                                                {symbol}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>

                                        {/* Mode Row */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    Mode
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={formik.values.mode}
                                                        onChange={formik.handleChange}
                                                        name="mode"
                                                        displayEmpty
                                                        sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="Demo">Demo</MenuItem>
                                                        <MenuItem value="Live">Live</MenuItem>
                                                        <MenuItem value="Paper">Paper</MenuItem>
                                                        <MenuItem value="Backtest">Backtest</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>

                                        {/* Enabled Row */}
                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    Enabled
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                            name="isEnabled"
                                                    checked={Boolean(formik.values.isEnabled)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('isEnabled', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#4caf50',
                                                        '&.Mui-checked': { color: '#4caf50' }
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* OrderTimeInForce Row */}
                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    OrderTimeInForce
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={formik.values.orderTimeInForce}
                                            onChange={formik.handleChange}
                                                        name="orderTimeInForce"
                                                        displayEmpty
                                            sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="GTC">GTC (Good Till Cancelled)</MenuItem>
                                                        <MenuItem value="IOC">IOC (Immediate or Cancel)</MenuItem>
                                                        <MenuItem value="FOK">FOK (Fill or Kill)</MenuItem>
                                                        <MenuItem value="DAY">DAY (Day Order)</MenuItem>
                                                        <MenuItem value="GTD">GTD (Good Till Date)</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Testing Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 20 }} />
                                        Testing Section
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        background: 'rgba(255, 255, 255, 0.02)', 
                                        borderRadius: 2, 
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        overflow: 'hidden'
                                    }}>
                                        {/* Table Header */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            background: 'rgba(6, 182, 212, 0.1)',
                                            borderBottom: '1px solid rgba(6, 182, 212, 0.2)'
                                        }}>
                                            <Box sx={{ flex: 1, p: 2, borderRight: '1px solid rgba(6, 182, 212, 0.2)' }}>
                                                <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                    Variable
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Typography variant="subtitle2" sx={{ color: '#bfc9db', fontWeight: 600 }}>
                                                    Value
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Testing Section Row */}
                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    Open Now
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={formik.values.openNow}
                                                        onChange={formik.handleChange}
                                                        name="openNow"
                                                        displayEmpty
                                                        sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="buy">Buy</MenuItem>
                                                        <MenuItem value="sell">Sell</MenuItem>
                                                        <MenuItem value="none">None</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Risk Management Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 20, color: '#06b6d4' }} />
                                        Risk Management
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        background: 'rgba(255, 255, 255, 0.02)', 
                                        borderRadius: 2, 
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    Enabled
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="riskManagementEnabled"
                                                    checked={Boolean(formik.values.riskManagementEnabled)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('riskManagementEnabled', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#9c27b0' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    WarmupMinutes
                                        </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="warmupMinutes"
                                                    value={formik.values.warmupMinutes}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MaxOrdersPerDay
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="maxOrdersPerDay"
                                                    value={formik.values.maxOrdersPerDay}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#f44336' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    OrderCooldownMs
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="orderCooldownMs"
                                                    value={formik.values.orderCooldownMs}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#f44336' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    OrderCooldownSeconds
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="orderCooldownSeconds"
                                                    value={formik.values.orderCooldownSeconds}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* CheckExistingPosition Row */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    CheckExistingPosition
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="checkExistingPosition"
                                                    checked={Boolean(formik.values.checkExistingPosition)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('checkExistingPosition', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* MaxOpenOrdersPerSide Row */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MaxOpenOrdersPerSide
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="maxOpenOrdersPerSide"
                                                    value={formik.values.maxOpenOrdersPerSide}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* MaxPositionDurationMinutes Row */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MaxPositionDurationMinutes
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="maxPositionDurationMinutes"
                                                    value={formik.values.maxPositionDurationMinutes}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* RequireMinimumBars Row */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    RequireMinimumBars
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="requireMinimumBars"
                                                    checked={Boolean(formik.values.requireMinimumBars)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('requireMinimumBars', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* LogRejectedOrders Row */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    LogRejectedOrders
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="logRejectedOrders"
                                                    checked={Boolean(formik.values.logRejectedOrders)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('logRejectedOrders', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* LogMetricsEveryNthTick Row */}
                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    LogMetricsEveryNthTick
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="logMetricsEveryNthTick"
                                                    value={formik.values.logMetricsEveryNthTick}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Spread Validation Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 20, color: '#9c27b0' }} />
                                        Spread Validation
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        background: 'rgba(255, 255, 255, 0.02)', 
                                        borderRadius: 2, 
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#e91e63' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MinAcceptableSpread
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="minAcceptableSpread"
                                                    value={formik.values.minAcceptableSpread}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MaxAcceptableSpread
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="maxAcceptableSpread"
                                                    value={formik.values.maxAcceptableSpread}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                    </Box>
                                </Box>

                                {/* Technical Indicators Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 20, color: '#e91e63' }} />
                                        Technical Indicators
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        background: 'rgba(255, 255, 255, 0.02)', 
                                        borderRadius: 2, 
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    Enabled
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="emaEnabled"
                                                    checked={Boolean(formik.values.emaEnabled)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('emaEnabled', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    Period
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="emaPeriod"
                                                    value={formik.values.emaPeriod}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* RequireBarsForInit Row */}
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    RequireBarsForInit
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="requireBarsForInit"
                                                    value={formik.values.requireBarsForInit}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        {/* MedianFilterSize Row */}
                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    MedianFilterSize
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="medianFilterSize"
                                                    value={formik.values.medianFilterSize}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Historical Data Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                                        Historical Data
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        background: 'rgba(255, 255, 255, 0.02)', 
                                        borderRadius: 2, 
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    BarPeriod
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="barPeriod"
                                                    value={formik.values.barPeriod}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    BarsToKeep
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="barsToKeep"
                                                    value={formik.values.barsToKeep}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    EnableDataCollection
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="enableDataCollection"
                                                    checked={Boolean(formik.values.enableDataCollection)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('enableDataCollection', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    SaveHistoricalBarsToDatabase
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="saveHistoricalBarsToDatabase"
                                                    checked={Boolean(formik.values.saveHistoricalBarsToDatabase)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('saveHistoricalBarsToDatabase', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Logging Configuration Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                                        Logging Configuration
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        background: 'rgba(255, 255, 255, 0.02)', 
                                        borderRadius: 2, 
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        overflow: 'hidden'
                                    }}>
                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    Enabled
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <Checkbox
                                                    name="loggingEnabled"
                                                    checked={Boolean(formik.values.loggingEnabled)}
                                                    onChange={(e) => {
                                                        formik.setFieldValue('loggingEnabled', e.target.checked);
                                                    }}
                                                    sx={{
                                                        color: '#06b6d4',
                                                        '&.Mui-checked': {
                                                            color: '#06b6d4',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    LogLevel
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={formik.values.logLevel}
                                                        onChange={formik.handleChange}
                                                        name="logLevel"
                                                        sx={{
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            color: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        }}
                                                    >
                                                        <MenuItem value="Debug">Debug</MenuItem>
                                                        <MenuItem value="Info">Info</MenuItem>
                                                        <MenuItem value="Warning">Warning</MenuItem>
                                                        <MenuItem value="Error">Error</MenuItem>
                                                        <MenuItem value="Critical">Critical</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
                                            <Box sx={{ 
                                                flex: 1, 
                                                p: 2, 
                                                borderRight: '1px solid rgba(6, 182, 212, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}>
                                                <InfoIcon sx={{ fontSize: 16, color: '#06b6d4' }} />
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                    LogEveryNthTick
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    name="logEveryNthTick"
                                                    value={formik.values.logEveryNthTick}
                                                    onChange={formik.handleChange}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            '& fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.3)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#06b6d4',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#fff',
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                    </Box>
                                </Box>
                                    </>
                                )}
                                {/* End of V1 Form */}

                            </Box>
                        )}
                    </form>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            color: '#bfc9db',
                            borderColor: 'rgba(6, 182, 212, 0.3)',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                background: 'rgba(6, 182, 212, 0.1)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={formik.handleSubmit}
                        disabled={loading || !formik.isValid}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{
                            background: '#ff9800',
                            color: '#000',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#f57c00'
                            },
                            '&:disabled': {
                                background: 'rgba(255, 152, 0, 0.3)',
                                color: 'rgba(0, 0, 0, 0.3)'
                            }
                        }}
                    >
                        {loading ? 'Updating...' : 'Update Strategy'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default EditStrategyModal;
